/**
 * The live pairing state of Add machine (ADR 0042 decision 1, "download, run, approve"; wire
 * contract in packages/protocol/src/ext/pairing.ts).
 *
 * THE SEAM. Everything the dialog knows about a computer being paired comes through one
 * `PairingSource`, picked by `pairingSource()` below:
 *   - demo data (?mock=1): `mockPairing`, a computer that shows up a few seconds after the
 *     dialog opens and joins once approved;
 *   - a server: `hubPairing`, the console half of the contract (list the requests waiting for
 *     approval, approve, deny).
 * Which requests a console may see (ADR 0042, the security session's rule): an unclaimed request
 * is never shown to anyone. A console on loopback sees the unclaimed requests of its own computer
 * and approves them in place. A remote console sees nothing before approval: the installer opens
 * a browser on the new computer, the person approves there, and this dialog goes from waiting
 * straight to connected when the machine reports (`consoleReach`, `usePairing`). The server half (the hub's pairing module, the control plane and
 *     the approve page /pair/:id) is built separately; until a server has it, its list answers
 *     404 and the dialog simply waits for a new machine to report, which also covers the
 *     developer path (a checkout started with a join command).
 * To plug in live push instead of polling (an `ext pairing` SSE event), replace `hubPairing.pending`
 * or feed `pairingPhase` from the store; nothing else in the dialog changes.
 */
import { PAIR_API, isPairFinal } from '@orgus/protocol';
import type { Machine, PairApproveRequest, PairDenyRequest, PairRequestView, ProjectSuggestion } from '@orgus/protocol';
import { api } from '../../lib/api.ts';
import { mockAgentInstallJoined } from './agentInstall.ts';
import { useOrgus } from '../../lib/store.ts';

/** loopback: this console runs on the computer it serves, so approve in place. remote: approval happens on the new computer. */
export type ConsoleReach = 'loopback' | 'remote';

/** The console's own host is this computer: localhost, 127.0.0.0/8, ::1 or a *.localhost name. */
export function isLoopbackHost(hostname: string): boolean {
  const h = hostname.toLowerCase().replace(/^\[|\]$/g, '');
  return h === 'localhost' || h.endsWith('.localhost') || h === '::1' || /^127(\.\d{1,3}){3}$/.test(h);
}

export const consoleReach = (hostname: string): ConsoleReach => (isLoopbackHost(hostname) ? 'loopback' : 'remote');

export interface PairingSource {
  /** Requests a signed-in person may approve now (claimed), newest first. A 404 means the server has no pairing yet. */
  pending(signal?: AbortSignal): Promise<PairRequestView[]>;
  approve(requestId: string, body: PairApproveRequest): Promise<PairRequestView>;
  deny(requestId: string, body?: PairDenyRequest): Promise<PairRequestView>;
}

/** The console half of the pairing contract, against the server this console talks to. */
export const hubPairing: PairingSource = {
  pending: (signal) => api<PairRequestView[]>(`${PAIR_API}?state=claimed`, { signal, timeoutMs: 10_000 }),
  approve: (id, body) => api<PairRequestView>(`${PAIR_API}/${encodeURIComponent(id)}/approve`, { method: 'POST', body }),
  deny: (id, body = {}) => api<PairRequestView>(`${PAIR_API}/${encodeURIComponent(id)}/deny`, { method: 'POST', body }),
};

/** Where the dialog's live state comes from: demo data in ?mock=1, else the server. */
export function pairingSource(): PairingSource {
  return useOrgus.getState().mode === 'mock' ? mockPairing : hubPairing;
}

/**
 * waiting    nothing to approve yet: the person downloads and opens the file on the new computer
 * approve    that computer asked to join and a person opened its request: approve it here
 * joining    approved; waiting for its first report
 * connected  it reports (also reached when any new machine connects, as on the developer path)
 * ended      the request was declined, expired, cancelled or stopped as a conflict
 */
export type PairingPhase =
  | { kind: 'waiting' }
  | { kind: 'approve'; request: PairRequestView }
  | { kind: 'joining'; request: PairRequestView }
  | { kind: 'connected'; machine: Machine }
  | { kind: 'ended'; request: PairRequestView };

export interface PairingInput {
  /** What the source listed last (newest first). */
  requests: readonly PairRequestView[];
  machines: readonly Machine[];
  /** Machines that existed when the dialog opened. */
  known: ReadonlySet<string>;
  /** When the dialog opened: only requests made after it (with a minute of slack) belong to it. */
  openedAt: number;
  now: number;
}

const SLACK_MS = 60_000;

/** The next phase, from the previous one and what the source and the store say now. Pure. */
export function pairingPhase(prev: PairingPhase, x: PairingInput): PairingPhase {
  if (prev.kind === 'connected') return prev;
  const fresh = x.machines.filter((m) => !x.known.has(m.id) && m.online);
  if (fresh.length) {
    const host = prev.kind === 'joining' || prev.kind === 'approve' ? prev.request.machine.hostname.toLowerCase() : null;
    const match = (host && fresh.find((m) => m.hostname.toLowerCase() === host || m.name.toLowerCase() === host)) || fresh[0]!;
    return { kind: 'connected', machine: match };
  }
  if (prev.kind === 'joining') return prev;
  if (prev.kind === 'approve') {
    const now = x.requests.find((r) => r.requestId === prev.request.requestId);
    if (now && isPairFinal(now.state) && now.state !== 'approved') return { kind: 'ended', request: now };
    if (now?.state === 'approved') return { kind: 'joining', request: now };
    if (!now && x.now >= prev.request.expiresAt) return { kind: 'ended', request: { ...prev.request, state: 'expired' } };
    if (now) return { kind: 'approve', request: now };
  }
  // A claimed request was opened by a signed-in person (the browser holding its code, or its three
  // words), so it belongs here whenever it was made: someone may type the words first and open
  // Add machine later. An unclaimed one belongs to this dialog only when made after it opened.
  const open = x.requests.find((r) => r.expiresAt > x.now && (r.state === 'claimed' || (r.state === 'unclaimed' && r.createdAt >= x.openedAt - SLACK_MS)));
  if (open) return { kind: 'approve', request: open };
  return prev.kind === 'ended' ? prev : { kind: 'waiting' };
}

// ── Demo data ────────────────────────────────────────────────────────────────

/** A forced dialog state for screenshots and tests: `?add=<preset>` on Machines in demo mode. */
export type AddMachinePreset = 'waiting' | 'approve' | 'connected' | 'unavailable' | 'error' | 'developer' | 'local';
export const ADD_MACHINE_PRESETS: readonly AddMachinePreset[] = ['waiting', 'approve', 'connected', 'unavailable', 'error', 'developer', 'local'];

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

function demoRequest(now: number): PairRequestView {
  return {
    requestId: 'pr_demo',
    state: 'claimed',
    flow: 'browser',
    createdAt: now,
    expiresAt: now + 10 * 60_000,
    fingerprint: 'K7Q2-9XMA-4HWN-PR3T',
    machine: { hostname: 'build-04', os: 'windows', osVersion: 'Windows 11 Pro 24H2', arch: 'amd64', agentVersion: '0.9.1', user: 'alex' },
    harnesses: [{ id: 'claude-code', version: '2.1.4', signedIn: true }],
    differentNetwork: false,
    claimedBy: { memberId: 'u_you', name: 'You' },
  };
}

/** The new machine as it reports once approved. */
function demoMachine(r: PairRequestView, now: number): Machine {
  return {
    id: `m-${r.machine.hostname}-${now.toString(36)}`,
    name: r.machine.hostname,
    os: r.machine.os === 'darwin' ? 'macos' : r.machine.os,
    arch: r.machine.arch === 'amd64' ? 'x64' : 'arm64',
    hostname: r.machine.hostname,
    cpus: 16,
    memTotal: 64 * 1024 ** 3,
    memFree: 51 * 1024 ** 3,
    load: 0.06,
    agentVersion: r.machine.agentVersion,
    connectedAt: now,
    lastSeen: now,
    online: true,
    labels: [],
    // What its agent found (R-ON-4): the repositories it names are its suggestions below.
    readiness: {
      checkedAt: now,
      harnesses: [
        { kind: 'claude-code', installed: true, version: '2.1.4 (Claude Code)', signIn: 'signed-in' },
        { kind: 'codex', installed: false, version: null, signIn: 'unknown' },
      ],
      git: { installed: true, version: '2.47.1.windows.1', identityConfigured: true },
      repos: { count: DEMO_REPOS.length, paths: DEMO_REPOS.map((r) => `source/repos/${r}`), truncated: false },
    },
  };
}

const DEMO_REPOS = ['payments-api', 'storefront', 'infra'];

/** The repositories the new machine's agent suggests, as recent Claude Code folders. */
function demoSuggestions(m: Machine, user: string, now: number): ProjectSuggestion[] {
  return DEMO_REPOS.map((name, i) => ({ machineId: m.id, path: `C:/Users/${user}/source/repos/${name}`, name, remoteUrl: null, lastSessionAt: now - (i + 1) * 3_600_000 }));
}

/** Demo pairing: a computer asks to join a few seconds after the dialog first lists, and reports once approved. */
export const mockPairing: PairingSource & { reset(preset?: AddMachinePreset, reach?: ConsoleReach): void } = (() => {
  let requests: PairRequestView[] = [];
  let appearsAt: number | null = null;
  let preset: AddMachinePreset | undefined;
  let remoteTimer: ReturnType<typeof setTimeout> | null = null;
  const join = (r: PairRequestView) => {
    const s = useOrgus.getState();
    if (s.mode !== 'mock') return;
    const now = Date.now();
    const m = demoMachine(r, now);
    s.applyPatch({ at: now, upserts: { machines: [m], suggestions: demoSuggestions(m, r.machine.user, now) }, removes: {} });
    s.applyEvent({ id: `f-join-${now}`, at: now, kind: 'machine.online', title: `${m.name} connected`, machineId: m.id, projectId: null });
    mockAgentInstallJoined(m.id, now);
  };
  return {
    reset(p, reach = 'loopback') {
      preset = p;
      requests = [];
      appearsAt = null;
      if (remoteTimer) clearTimeout(remoteTimer);
      remoteTimer = null;
      // Remote: the person approves on the new computer; here the machine simply reports a few seconds later.
      if (reach === 'remote' && p === undefined) remoteTimer = setTimeout(() => join({ ...demoRequest(Date.now()), state: 'approved' }), 6000);
      if (p === 'approve' || p === 'connected') requests = [demoRequest(Date.now())];
      if (p === 'connected') {
        requests = [{ ...requests[0]!, state: 'approved' }];
        join(requests[0]!);
      }
    },
    async pending() {
      await delay(150);
      if (preset === 'waiting' || preset === 'unavailable' || preset === 'error' || preset === 'developer' || preset === 'local') return [];
      const now = Date.now();
      if (!requests.length) {
        appearsAt ??= now + 6000;
        if (now >= appearsAt) requests = [demoRequest(now)];
      }
      return requests.filter((r) => r.state === 'claimed' || r.state === 'approved' || r.state === 'denied');
    },
    async approve(id, body) {
      await delay(500);
      const r = requests.find((x) => x.requestId === id);
      if (!r) throw new Error('That request is no longer open.');
      const now = Date.now();
      const done: PairRequestView = { ...r, state: 'approved', decidedBy: { memberId: 'u_you', name: 'You', at: now }, machine: { ...r.machine, hostname: body.name ?? r.machine.hostname } };
      requests = requests.map((x) => (x.requestId === id ? done : x));
      setTimeout(() => join(done), 1200);
      return done;
    },
    async deny(id) {
      await delay(400);
      const r = requests.find((x) => x.requestId === id);
      if (!r) throw new Error('That request is no longer open.');
      const done: PairRequestView = { ...r, state: 'denied', decidedBy: { memberId: 'u_you', name: 'You', at: Date.now() } };
      requests = requests.map((x) => (x.requestId === id ? done : x));
      return done;
    },
  };
})();
